// ShimmerClock — periodic gold-wash overlay coordinator for money pills.
//
// Every scene that hosts shimmering pills owns one clock. The clock holds
// master phase + config; each pill calls `localPhase()` per frame to learn
// whether it's currently inside its active window, and if so what fraction
// (0..1) of that window it's progressed through.
//
// All non-Phaser logic lives in pure helpers exported below the class so it
// can be unit-tested without a scene fixture.

export type ShimmerMode = 'off' | 'band' | 'rim';
export type ShimmerSpeed = 'slow' | 'med' | 'fast';
export type ShimmerIntensity = 'subtle' | 'med' | 'strong';
export type ShimmerSync = 'wave' | 'burst' | 'staggered';

export interface ShimmerConfig {
  mode: ShimmerMode;
  speed: ShimmerSpeed;
  intensity: ShimmerIntensity;
  sync: ShimmerSync;
  pauseDuringRoll: boolean;
}

export interface SpeedPreset {
  /** Wave-to-wave gap. */
  periodMs: number;
  /** Time for the wave to cross the full screen width. */
  sweepDurationMs: number;
  /** Time the band/rim takes to cross a single pill. */
  perPillDurationMs: number;
}

export const SPEED_PRESETS: Record<ShimmerSpeed, SpeedPreset> = {
  slow: { periodMs: 8000, sweepDurationMs: 5000, perPillDurationMs: 1000 },
  med:  { periodMs: 6000, sweepDurationMs: 3500, perPillDurationMs: 700 },
  fast: { periodMs: 4000, sweepDurationMs: 2000, perPillDurationMs: 500 },
};

export const INTENSITY_PEAK_ALPHA: Record<ShimmerIntensity, number> = {
  subtle: 0.20,
  med: 0.35,
  strong: 0.50,
};

export const DEFAULT_SHIMMER_CONFIG: ShimmerConfig = {
  mode: 'band',
  speed: 'fast',
  intensity: 'med',
  sync: 'wave',
  pauseDuringRoll: true,
};

/**
 * Stable, deterministic [0, 1) offset for a pill id. Used by `staggered` sync
 * mode so each pill's per-period arrival time is fixed across periods. The
 * Mulberry32 step gives enough variation across small ids that adjacent
 * pills don't visibly cluster.
 */
export function pillStableOffset(id: number): number {
  let s = (id + 0x9e3779b9) >>> 0;
  s = Math.imul(s ^ (s >>> 16), 0x85ebca6b) >>> 0;
  s = Math.imul(s ^ (s >>> 13), 0xc2b2ae35) >>> 0;
  s = (s ^ (s >>> 16)) >>> 0;
  return s / 0x100000000;
}

export interface ComputeLocalPhaseInput {
  tNowMs: number;
  config: ShimmerConfig;
  speed: SpeedPreset;
  paused: boolean;
  pill: { id: number; x: number; screenW: number };
}

/**
 * Pure phase-math: returns the pill's progress through its active window in
 * [0, 1), or null if the clock is off / paused / outside the pill's window
 * for the current period.
 */
export function computeLocalPhase(input: ComputeLocalPhaseInput): number | null {
  const { tNowMs, config, speed, paused, pill } = input;
  if (paused) return null;
  if (config.mode === 'off') return null;

  const tInPeriod = ((tNowMs % speed.periodMs) + speed.periodMs) % speed.periodMs;
  let tArrival: number;
  switch (config.sync) {
    case 'wave': {
      const xClamped = pill.screenW > 0 ? Math.max(0, Math.min(pill.x, pill.screenW)) : 0;
      const ratio = pill.screenW > 0 ? xClamped / pill.screenW : 0;
      tArrival = ratio * speed.sweepDurationMs;
      break;
    }
    case 'burst':
      tArrival = 0;
      break;
    case 'staggered': {
      const usable = Math.max(0, speed.periodMs - speed.perPillDurationMs);
      tArrival = pillStableOffset(pill.id) * usable;
      break;
    }
  }

  if (tInPeriod < tArrival) return null;
  if (tInPeriod >= tArrival + speed.perPillDurationMs) return null;

  const local = (tInPeriod - tArrival) / speed.perPillDurationMs;
  return local;
}

import type Phaser from 'phaser';

export type PillToken = number & { readonly __brand: 'PillToken' };

declare global {
  interface Window {
    __shimmerClocks?: Set<ShimmerClock>;
  }
}

function getRegistry(): Set<ShimmerClock> {
  if (typeof window === 'undefined') {
    // SSR / test fallback — the registry is only consumed by the dev panel,
    // which itself short-circuits when there's no `window`.
    return new Set();
  }
  if (!window.__shimmerClocks) window.__shimmerClocks = new Set();
  return window.__shimmerClocks;
}

export class ShimmerClock {
  private scene: Phaser.Scene;
  private config: ShimmerConfig = { ...DEFAULT_SHIMMER_CONFIG };
  private paused = false;
  private nextPillId = 1;
  /**
   * Offset added to `scene.time.now` so that `forceSweep()` can move "now" to
   * a period boundary without depending on real time.
   */
  private periodOriginMs = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    getRegistry().add(this);
  }

  setConfig(partial: Partial<ShimmerConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  getConfig(): ShimmerConfig {
    return { ...this.config };
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  /** Force the next sweep to start at "now" — used by the dev panel button. */
  forceSweep(): void {
    this.periodOriginMs = this.scene.time.now;
  }

  /** Allocate a per-pill identity used by `staggered` sync. */
  register(): PillToken {
    const id = this.nextPillId++;
    return id as PillToken;
  }

  /** No-op today — pillStableOffset is pure on the id, so no per-pill state
   *  needs to leak. Kept on the public surface for symmetry and so a future
   *  staggered implementation that DOES carry state can hook in without
   *  changing call sites. */
  unregister(_token: PillToken): void {
    // intentionally empty
  }

  localPhase(token: PillToken, x: number, screenW: number): number | null {
    if (this.destroyed) return null;
    const tNowMs = this.scene.time.now - this.periodOriginMs;
    return computeLocalPhase({
      tNowMs,
      config: this.config,
      speed: SPEED_PRESETS[this.config.speed],
      paused: this.paused,
      pill: { id: token as number, x, screenW },
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    getRegistry().delete(this);
  }
}
